import * as React from 'react'
import { Col, Icon, Row, Spin } from 'antd'
import { compose, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { firebaseConnect } from 'react-redux-firebase'
import { isMobile } from 'react-device-detect'
import classNames from 'classnames'
import Container from '../../components/Container'
import TimeWindow from '../../components/TimeWindow/TimeWindow'
import styles from './DeliveryTracking.module.scss'
import DeliveryTimeline from '../../components/DeliveryTimeline/DeliveryTimeline'
import { ApplicationState } from '../../store'
import { DeliveryTrackingData } from '../../store/DeliveryTracking/types'
import NotFound from '../../components/NotFound/NotFound'
import DeliveryReschedule from '../../components/DeliveryReschedule/DeliveryReschedule'
import * as DeliveryTrackingActions from '../../store/DeliveryTracking/actions'
import DeliveryIncomplete from '../../components/DeliveryIncomplete/DeliveryIncomplete'
import env from '../../lib/env'
import * as AnalyticsManager from '../../lib/analytics/analytics'
import { AnalyticsEvent, PageTypes } from '../../lib/analytics/events'

export const deliveryHashKey = 'lastDeliveryHash'

class DeliveryTracking extends React.Component<AllProps> {
  style: CSSStyleDeclaration = document.documentElement.style

  componentDidMount(): void {
    AnalyticsManager.setRelayEventProperty('page_type', PageTypes.Tracking)
    setTimeout(() => {
      AnalyticsManager.sendEvent(AnalyticsEvent.ApplicationOpened)
      AnalyticsManager.sendCurrentPageView()
    }, 1000)
  }

  render() {
    const {
      deliveryData: {
        uuid,
        cancellableByRecipient,
        country,
        status,
        scheduledAt,
        deliverFrom,
        deliverUntil,
        inRouteFrom,
        willArriveAt,
        companyInfo: {
          displayName,
          template: {
            primaryColor,
          },
        },
      },
      loading,
      found,
      showReschedule,
      rescheduleOpen,
      rescheduleClose,
      rescheduleRequest,
      rescheduleRequested,
    } = this.props
    this.style.setProperty('--primary-color', primaryColor)
    const spinnerIcon = <Icon type="loading" className={styles.spinnerIcon} />

    if (found) {
      const getHash = (props: any) => { return props.match.params.uuid }
      localStorage.setItem(deliveryHashKey, getHash(this.props))
    }

    const rescheduleClasses: any = {}
    rescheduleClasses[styles.reschedule] = true
    rescheduleClasses[styles.mobile] = isMobile
    rescheduleClasses[styles.showReschedule] = showReschedule

    AnalyticsManager.setProperty(AnalyticsManager.AnalyticsProperty.DeliveryUUID, uuid)
    AnalyticsManager.setRelayEventProperty('country', country)

    if (displayName) {
      AnalyticsManager.setProperty(AnalyticsManager.AnalyticsProperty.CompanyName, displayName)
    }

    if (status) {
      AnalyticsManager.setProperty(AnalyticsManager.AnalyticsProperty.Status, status)
    }

    if (!loading && !found && !rescheduleRequested) {
      return <NotFound inAnotherPage />
    }

    return (
      <Container className={styles.container}>
        <Spin spinning={loading} size="large" className={styles.spinner} indicator={spinnerIcon} />
        <Row>
          <Col xs={24} className={classNames(rescheduleClasses)}>
            <DeliveryReschedule
              onRescheduleRequest={() => rescheduleRequest(uuid)}
              onCancel={() => rescheduleClose()}
              rescheduleRequestSent={rescheduleRequested}
              show={showReschedule}
              color={primaryColor} />
          </Col>
        </Row>
        <Col xs={24}>
          <DeliveryIncomplete
            status={status}
            show={!showReschedule} />
        </Col>
        <Col xs={24}>
          <TimeWindow
            enableReschedule={cancellableByRecipient}
            scheduledAt={scheduledAt}
            deliverFrom={deliverFrom}
            deliverUntil={deliverUntil}
            status={status}
            onRescheduleClick={() => rescheduleOpen()}
            show={!showReschedule}
            color={primaryColor} />
        </Col>
        <Col xs={24} className={styles.timeline}>
          <DeliveryTimeline
            status={status}
            show={!showReschedule}
            color={primaryColor}
            inRouteFrom={inRouteFrom}
            willArriveAt={willArriveAt} />
        </Col>
      </Container>
    )
  }
}

interface PropsFromState {
  deliveryData: DeliveryTrackingData
  loading: boolean
  found: boolean
  showReschedule: boolean
  rescheduleRequested: boolean
}

interface PropsFromDispatch {
  rescheduleOpen: typeof DeliveryTrackingActions.rescheduleOpen,
  rescheduleClose: typeof DeliveryTrackingActions.rescheduleClose,
  rescheduleRequest: typeof DeliveryTrackingActions.rescheduleRequest
}

type AllProps = PropsFromState & PropsFromDispatch

const mapStateToProps = ({ containers }: ApplicationState) => ({
  deliveryData: containers.DeliveryTracking.deliveryData,
  loading: containers.DeliveryTracking.loading,
  found: containers.DeliveryTracking.found,
  showReschedule: containers.DeliveryTracking.showReschedule,
  rescheduleRequested: containers.DeliveryTracking.rescheduleRequested,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  rescheduleOpen: () => dispatch(DeliveryTrackingActions.rescheduleOpen()),
  rescheduleClose: () => dispatch(DeliveryTrackingActions.rescheduleClose()),
  rescheduleRequest: (deliveryUuid: string) => dispatch(DeliveryTrackingActions.rescheduleRequest(deliveryUuid)),
})

export default compose(
  firebaseConnect((props: {match: {params: {uuid: string}}}) => {
    return [{
      path: `${env()}/delivery/${props.match.params.uuid || 'null'}`,
    }]
  }),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(DeliveryTracking)
